import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/data';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 group cursor-pointer">
            <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center">
              <i className={`ri-vidicon-line text-2xl md:text-3xl ${
                scrolled || !isHome ? 'text-primary-500' : 'text-background-50'
              }`}></i>
            </div>
            <span className={`text-lg md:text-xl font-heading font-bold ${
              scrolled || !isHome ? 'text-foreground-950' : 'text-background-50'
            }`}>
              黄瓜视频
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                location.pathname === '/'
                  ? scrolled || !isHome
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-background-50 bg-white/20'
                  : scrolled || !isHome
                    ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-white/15'
              }`}
            >
              首页
            </Link>

            <div className="relative">
              <button
                onMouseEnter={() => setCategoryOpen(true)}
                onMouseLeave={() => setCategoryOpen(false)}
                onClick={() => setCategoryOpen(!categoryOpen)}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  location.pathname.startsWith('/category')
                    ? scrolled || !isHome
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-background-50 bg-white/20'
                    : scrolled || !isHome
                      ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'
                      : 'text-background-50/90 hover:text-background-50 hover:bg-white/15'
                }`}
              >
                分类浏览
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${categoryOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {categoryOpen && (
                <div
                  onMouseEnter={() => setCategoryOpen(true)}
                  onMouseLeave={() => setCategoryOpen(false)}
                  className="absolute top-full left-0 mt-1 w-48 bg-background-50 rounded-lg border border-background-200/70 py-2 z-50"
                >
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-base`}></i>
                      </div>
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                location.pathname === '/about'
                  ? scrolled || !isHome
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-background-50 bg-white/20'
                  : scrolled || !isHome
                    ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'
                    : 'text-background-50/90 hover:text-background-50 hover:bg-white/15'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="#download"
              className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              立即下载
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer ${
              scrolled || !isHome ? 'text-foreground-700' : 'text-background-50'
            }`}
            aria-label="菜单"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/70 py-3 animate-in">
            <Link
              to="/"
              className={`block px-4 py-3 rounded-lg text-sm font-medium cursor-pointer ${
                location.pathname === '/' ? 'text-primary-600 bg-primary-50' : 'text-foreground-700'
              }`}
            >
              首页
            </Link>

            <div className="px-4 py-3 text-sm font-medium text-foreground-500">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-folder-line"></i>
                </div>
                分类浏览
              </div>
              <div className="grid grid-cols-2 gap-1 mt-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground-600 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={cat.icon}></i>
                    </div>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/about"
              className={`block px-4 py-3 rounded-lg text-sm font-medium cursor-pointer ${
                location.pathname === '/about' ? 'text-primary-600 bg-primary-50' : 'text-foreground-700'
              }`}
            >
              关于我们
            </Link>

            <div className="px-4 pt-3">
              <a
                href="#download"
                className="block w-full text-center px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer"
              >
                立即下载
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}